/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190902 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { ReasonsService } from '../reasons.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { ReasonsDaoService } from '../../dao/reasons-dao.service';
import { IMSId } from 'src/app/_model/ims-object';
import { Reason, ReasonDTO } from 'src/app/_model/reason';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { APILevel } from 'src/app/_services/sync/api-level';
import { AppParameterService } from 'src/app/_services/app-parameter/logic/app-parameter.service';


/**
 * Loads Reasons
 */
@Injectable()
export class ReasonsLogicService implements ReasonsService {
  public reasons$ = new BehaviorSubject<Reason[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> = new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private reasonsDaoService: ReasonsDaoService,
        private appParameterService: AppParameterService,
  ) {
    this.initialize();
  }

  // Public Function
  public getReasonById(id: IMSId): Reason {
    if (id === undefined) {
      return undefined
    }

    return this.reasons$.getValue().find((reason) => {
      if (reason.id.imsId === id.imsId && reason.id.secondaryId === id.secondaryId) {
        return true;
      }
      return false;
    });
  }

   public getReasonByType(type: string): Reason[] {
    if (type === undefined || type === "") {
      return this.reasons$.getValue();
    }

    return this.reasons$.getValue().filter((reason) => {
      if (reason.type === type) {
        return true;
      }
      return false;
    });
  }

  public getReasonByCode(code: number): Reason {
    if (code === undefined) {
      return undefined
    }

    return this.reasons$.getValue().find((reason) => {
      if (reason.code === code) {
        return true;
      }
      return false;
    });
  }

  public async updateReasons(reasonsDto: ReasonDTO[]): Promise<void> {
    this.logger.log(
      this.constructor.name,
      '- Received new reasons from other IMS component',
      reasonsDto
    );

    try {
      let reasons: Reason[] = [];

      for (const reasonDto of reasonsDto) {
        reasons.push(Reason.fromDto(reasonDto, undefined, this.appParameterService.getFormatLabelForObject('ims/Reason'),));
      }

      await this.reasonsDaoService.saveReasons(reasons);
      await this.reasons$.next(reasons);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error parsing Reasons: ',
        error.message
      );
      throw error;
    }
  }

  // Resetable
  public async reset() {
    await this.reasonsDaoService.reset();
    await this.loadLocalReasons();
  }

  public async clear() {
    await this.reasonsDaoService.clear();
    this.reasons$.next([]);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> { }

  public async presync(): Promise<void> { }

  public async postsync(): Promise<void> { }


  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING)
    try {
      await this.loadLocalReasons();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED)
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE)
    }
  }

  // Private Functions
  private async loadLocalReasons() {
    try {
      let reasons: Reason[] = await this.reasonsDaoService.getReasons();
      this.logger.log(
        this.constructor.name,
        '- Loaded local Reasons',
        reasons.length
      );
      this.reasons$.next(reasons);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local reasons: ',
        error.message
      );
      throw error;
    }
  }

  async initializeCompleted(): Promise<void> {

  }

}
